/**
 * Ce que l'appareil garde entre deux visites — 25/08/2026.
 *
 * ## Le défaut qui a produit ce fichier
 *
 * L'identifiant de session était « tiré au hasard et **jamais persisté** ».
 * C'était un choix de discrétion, et il rendait le parrainage
 * mathématiquement impossible.
 *
 * Le lien de parrainage embarque cet identifiant. Le seuil de la contrepartie
 * est de deux filleuls. Un parrain partageait donc son lien, fermait son
 * onglet, revenait le lendemain — et il était quelqu'un d'autre : le serveur,
 * interrogé avec un identifiant neuf, répondait zéro. Les filleuls de la
 * veille restaient crédités à une session qui n'existait plus et que personne
 * ne pouvait réclamer. Le compteur n'atteignait deux que si les deux filleuls
 * terminaient leur diagnostic **pendant que l'onglet d'origine était encore
 * ouvert**.
 *
 * `CLAUDE.md` désigne pourtant le taux de parrainage comme « l'indicateur à
 * suivre avant tout autre ». Cet indicateur ne pouvait pas monter.
 *
 * ## Ce que ce fichier change, et ce qu'il ne change pas
 *
 * Il rend l'identifiant **durable sur l'appareil**. Il ne le rend ni nominatif
 * ni partagé : c'est toujours un nombre tiré au hasard, il ne voyage pas d'un
 * appareil à l'autre, il n'est relié à aucun nom, et rien ne permet d'en
 * déduire qui que ce soit. Ce qui change est qu'il **dure**.
 *
 * Cela reste un identifiant durable posé sur l'appareil de quelqu'un, et cela
 * se dit : la politique de confidentialité le mentionne, et
 * `oublierCetAppareil()` l'efface. Une chose qu'on pose sans pouvoir la
 * retirer n'est pas un choix qu'on offre.
 *
 * ## La recette ne persiste jamais
 *
 * Le parcours joué après chaque livraison passe par les mêmes écrans qu'un
 * visiteur. Son identifiant porte un préfixe pour que le serveur l'écrive sans
 * le compter — sans quoi chaque déploiement créditerait des visites et des
 * ventes qui n'existent pas, le faux signal que la règle n° 6 interdit sur le
 * chiffre qui décide de tout.
 *
 * Il est donc tiré à neuf **à chaque fois**, et le stockage n'est ni lu ni
 * écrit dans ce mode. Le format validé à la relecture (`^[a-z0-9]{1,32}$`)
 * refuse au passage tout ce qui contient un tiret : un identifiant de recette
 * ne peut pas être relu par un visiteur, même si l'un d'eux traînait.
 */

const CLE_SID = "stratera.sid";
const CLE_PARRAIN = "stratera.parrain";
/*
  La trace d'achat, nommée ici plutôt qu'importée d'`achat.ts`.

  L'import croisé ferait dépendre l'identité de l'achat pour une seule chaîne,
  alors que le sens va dans l'autre sens. Le contrôle de `oubli.test.ts` tient
  les deux noms accordés — c'est la jointure, et elle est gardée.
*/
const CLE_ACHAT = "stratera.achat";

/** Le format d'un identifiant. Refuse le préfixe de recette, qui porte un tiret. */
const FORMAT = /^[a-z0-9]{1,32}$/i;

function estRecette(): boolean {
  return (
    typeof window !== "undefined" &&
    (window as { __strateraRecette?: unknown }).__strateraRecette === true
  );
}

function tirer(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Lit une valeur de l'appareil, ou `null`.
 *
 * Le `try` n'est pas décoratif : en navigation privée, avec les données de
 * site bloquées, ou dans une vue intégrée, l'accès **lève** au lieu de rendre
 * vide. Sans lui, l'application ne démarrerait pas du tout — et elle doit
 * démarrer, simplement sans mémoire.
 */
function lire(cle: string): string | null {
  try {
    const valeur = window.localStorage.getItem(cle);
    return valeur !== null && FORMAT.test(valeur) ? valeur : null;
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string): void {
  try {
    window.localStorage.setItem(cle, valeur);
  } catch {
    /* Rien à faire, et rien à signaler : le produit fonctionne sans mémoire. */
  }
}

/**
 * L'identifiant de cet appareil. Durable, sauf en recette.
 *
 * Quand le stockage est indisponible, on retombe sur l'ancien comportement —
 * un identifiant de session. Le parrainage ne fonctionnera pas pour cette
 * personne-là, et c'est préférable à une application qui ne s'ouvre pas.
 */
export function sidDurable(): string {
  if (estRecette()) return `recette-${tirer()}`;
  if (typeof window === "undefined") return tirer();
  const garde = lire(CLE_SID);
  if (garde !== null) return garde;
  const neuf = tirer();
  ecrire(CLE_SID, neuf);
  return neuf;
}

/**
 * Le parrain de cette personne, retenu au **premier** contact.
 *
 * Trois règles, et chacune répare un cas réel.
 *
 * **Le premier lien gagne.** Quelqu'un peut recevoir deux liens de deux
 * connaissances ; c'est celle qui l'a fait venir qui compte, et non la
 * dernière adresse ouverte. Sans cette règle, un second lien réattribuerait un
 * filleul déjà acquis.
 *
 * **Il survit à la fermeture de l'onglet.** C'était le défaut symétrique de
 * celui du `sid` : un filleul qui arrivait par un lien, regardait, et revenait
 * finir son diagnostic le lendemain n'était plus le filleul de personne.
 *
 * **On n'est pas son propre parrain.** Le lien qu'on partage passe par ses
 * propres mains — on le teste, on le rouvre pour vérifier. Sans ce refus, le
 * compteur monterait tout seul.
 */
export function retenirParrain(depuisUrl: string | null, sid: string): string | null {
  if (estRecette()) return depuisUrl;
  if (typeof window === "undefined") return depuisUrl;
  const garde = lire(CLE_PARRAIN);
  if (garde !== null) return garde;
  if (depuisUrl === null || depuisUrl === sid || !FORMAT.test(depuisUrl)) return null;
  ecrire(CLE_PARRAIN, depuisUrl);
  return depuisUrl;
}

/**
 * Efface ce que l'appareil garde.
 *
 * Offert dans les mentions de confidentialité. Le geste est local : il ne
 * demande rien au serveur, parce qu'il n'y a rien à lui demander — ces deux
 * valeurs ne vivent que sur l'appareil.
 */
export function oublierCetAppareil(): void {
  try {
    window.localStorage.removeItem(CLE_SID);
    window.localStorage.removeItem(CLE_PARRAIN);
    /*
      La trace d'achat part aussi — 02/09/2026.

      Elle ne partait pas, et le bouton répondait pourtant « cet appareil ne
      garde plus rien ». Or `stratera.achat` — identifiant de transaction, plan,
      date — reste trente jours, et l'écran de reprise le relit au démarrage.

      Le scénario qui tranche est celui d'un téléphone emprunté, courant sur ce
      marché : quelqu'un presse le bouton avant de rendre l'appareil, lit que
      rien n'est gardé, et le visiteur suivant trouve « Un paiement est
      enregistré sur cet appareil » avec le bouton qui ouvre le rapport.

      Ce que la personne perd en échange est dit à l'écran, sous le bouton : la
      reprise depuis cet appareil. Sa référence, reçue par courriel, rouvre le
      rapport depuis n'importe où.
    */
    window.localStorage.removeItem(CLE_ACHAT);
  } catch {
    /* Déjà sans mémoire. */
  }
}
